import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import { fetchAccountAvailableDomains, fetchAccountAvailableDomainsPools } from "../thunks/accountDomainsThunk";
import { type IdName } from "features/Library/SimulatedPhishing/Domains/types/state";
import { type AssignDomain } from "../../PackAssignmentModal/types";
import { getPrefix } from "../../../Application/slices/models";
import { ReducerEntityPrefixTypes, ReducerNamespaceTypes } from "../../../../enums/reducer";

export type AvailableDomainsConfigurationState = {
  useDomains: boolean;
  domainPools: IdName[];
  domains?: { [key: number]: IdName[] };
  domainTypes: IdName[];
  isLoading: boolean;
  selectedDomain: AssignDomain;
};

const initialState: AvailableDomainsConfigurationState = {
  useDomains: false,
  domainTypes: [],
  domainPools: [],
  domains: {},
  isLoading: false,
  selectedDomain: { isAssigned: false },
};

const availableDomainsSlice = createSlice({
  name: getPrefix({
    namespace: ReducerNamespaceTypes.Accounts,
    entity: ReducerEntityPrefixTypes.ThreatDefence,
    name: "availableDomains",
  }),
  initialState,
  reducers: {
    setSelectedDomain: (state, action: PayloadAction<AssignDomain>) => {
      state.selectedDomain = action.payload;
    },
    setSelectedDomainType: (state, action: PayloadAction<number>) => {
      state.selectedDomain = {
        domainType: action.payload,
        isAssigned: false,
      };
    },
    reset() {
      return { ...initialState };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAccountAvailableDomains.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchAccountAvailableDomains.fulfilled, (state, action) => {
        const domainPools = action.payload?.domainPools ?? [];
        const domainTypesObj = action.payload?.domainTypes ?? {};
        const availableDomains = action.payload?.availableDomains ?? {};

        state.domainPools = domainPools;
        state.domains = availableDomains;

        const hasDomainTypes = Object.keys(domainTypesObj).length > 0;
        state.useDomains = hasDomainTypes;

        state.domainTypes = hasDomainTypes
          ? Object.entries(domainTypesObj).map(([id, name]) => ({
              id: Number(id),
              name,
            }))
          : [{ id: 1, name: "Available Pool" }];

        state.isLoading = false;
      })
      .addCase(fetchAccountAvailableDomains.rejected, (state) => {
        state.isLoading = false;
      })
      .addCase(fetchAccountAvailableDomainsPools.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchAccountAvailableDomainsPools.fulfilled, (state, action) => {
        state.domains = action.payload.availableDomains;
        state.domainTypes = action.payload.domainTypes;

        state.useDomains = true;
        state.isLoading = false;
      })
      .addCase(fetchAccountAvailableDomainsPools.rejected, (state) => {
        state.isLoading = false;
      });
  },
});

export const { reset, setSelectedDomain, setSelectedDomainType } = availableDomainsSlice.actions;

export default availableDomainsSlice.reducer;
